import React, { useState, useRef, useEffect } from 'react';
import { Block, Flex, MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../../../components/common/StyledComponent/CommonStyledComponent';
import {
  ChatThread,
  ResponseContent,
  StreamingCursor,
  SuggestedButton,
  GeneratingLabel,
} from '../AskAIPageStyles';
import AskAIGeneratingResponse from './AskAIGeneratingResponse';
import AskAIResponseFeedback from './AskAIResponseFeedback';
import AskAISuggestionResponse from './AskAISuggestionResponse';
import { Helmet } from 'react-helmet';
import { getInfoHubHtml } from '../../../utility/common';
import DOMPurify from 'dompurify';

const isTablet =
    window.innerWidth > MOBILE_BREAKPOINT &&
    window.innerWidth <= TABLET_BREAKPOINT;
import AIRecommendationCarouselSection from './AIRecommendationCarouselSection.jsx';


const ChatEntryFeedback = ({ isMobile, vegasId }) => {
  const [selectedThumb, setSelectedThumb] = useState(null);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);
  const [isFeedbackClosing, setIsFeedbackClosing] = useState(false);

  const handleThumbClick = (thumbType) => {
    setSelectedThumb(thumbType);
    setIsFeedbackClosing(false);
    setShowFeedbackPanel(true);
  };

  const closeFeedbackPanel = () => {
    setIsFeedbackClosing(true);
  };

  const handleFeedbackAnimationEnd = () => {
    if (isFeedbackClosing) {
      setShowFeedbackPanel(false);
      setIsFeedbackClosing(false);
    }
  };

  return (
    <AskAIResponseFeedback
      selectedThumb={selectedThumb}
      onThumbClick={handleThumbClick}
      showFeedbackPanel={showFeedbackPanel}
      isFeedbackClosing={isFeedbackClosing}
      onCloseFeedback={closeFeedbackPanel}
      onFeedbackAnimationEnd={handleFeedbackAnimationEnd}
      isMobile={isMobile}
      vegasId={vegasId}
    />
  );
};

const renderFormattedText = (rawText) => {
  return rawText.split(/\n|\\n/).map((line, lineIdx, arr) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <React.Fragment key={lineIdx}>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
          }
          return part;
        })}
        {lineIdx < arr.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const ResponseText = ({ text, isStreaming }) => {
  if (!text && isStreaming) return null;
  return (
    <div className="vds-title-xs mobile-vds-title-2xs mb-8X sm-mb-6X ph-6X sm-ph-4X">
      {renderFormattedText(text)}
      {isStreaming && <StreamingCursor aria-hidden="true" />}
    </div>
  );
};

const SECTION_SCROLL_DURATION = 900;

const easeInOutCubic = (progress) => {
  if (progress < 0.5) {
    return 4 * progress * progress * progress;
  }

  return 1 - Math.pow(-2 * progress + 2, 3) / 2;
};

const getScrollableParent = (element) => {
  let parent = element?.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const canScroll = /(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight;

    if (canScroll) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return document.scrollingElement || document.documentElement;
};

const smoothScrollToElementTop = (element) => {
  if (!element) return () => {};

  const scrollContainer = getScrollableParent(element);
  const isWindowScrollTarget =
    scrollContainer === document.scrollingElement ||
    scrollContainer === document.documentElement ||
    scrollContainer === document.body;

  const startY = isWindowScrollTarget ? window.pageYOffset : scrollContainer.scrollTop;
  const targetY = isWindowScrollTarget
    ? window.pageYOffset + element.getBoundingClientRect().top
    : scrollContainer.scrollTop + (element.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top);
  const distance = targetY - startY;

  if (Math.abs(distance) < 2) {
    return () => {};
  }

  const startTime = performance.now();
  let animationFrameId = null;

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / SECTION_SCROLL_DURATION, 1);
    const eased = easeInOutCubic(progress);
    const currentY = startY + distance * eased;

    if (isWindowScrollTarget) {
      window.scrollTo({ top: currentY, left: 0, behavior: 'auto' });
    } else {
      scrollContainer.scrollTop = currentY;
    }

    if (progress < 1) {
      animationFrameId = window.requestAnimationFrame(animate);
    }
  };

  animationFrameId = window.requestAnimationFrame(animate);

  return () => {
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
    }
  };
};

const AskAIInitialResponse = ({
  custOverrideXfHtml,
  chatHistory = [],
  isChatLoading,
  handleSuggestionClick,
  selectedThumb,
  handleThumbClick,
  showFeedbackPanel,
  isFeedbackClosing,
  closeFeedbackPanel,
  handleFeedbackAnimationEnd,
  isMobile,
  isSearchDisabled,
  askAiData,
  onViewMixedResults,
}) => {
  const followUpResponseRefs = useRef([]);
  const previousFollowUpCountRef = useRef(0);
  const hasInitializedFollowUpScrollRef = useRef(false);
  const firstEntry = chatHistory[0];
  const followUpEntries = chatHistory.slice(1);
  const firstDisplayText = firstEntry?.streamingText || firstEntry?.finalText || '';
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [isSourcesClosing, setIsSourcesClosing] = useState(false);
console.log('^^chatHistory,firstEntry,followUpEntries', chatHistory,firstEntry,followUpEntries);
  const [aemOverrideDataByIndex, setAemOverrideDataByIndex] = useState({});
  // Auto-scroll only when a new follow-up response is appended.
  useEffect(() => {
    const currentFollowUpCount = followUpEntries.length;

    // Skip auto-scroll on initial mount to avoid jumping on first render.
    if (!hasInitializedFollowUpScrollRef.current) {
      previousFollowUpCountRef.current = currentFollowUpCount;
      hasInitializedFollowUpScrollRef.current = true;
      return undefined;
    }

    const previousFollowUpCount = previousFollowUpCountRef.current;

    if (currentFollowUpCount <= previousFollowUpCount || currentFollowUpCount === 0) {
      previousFollowUpCountRef.current = currentFollowUpCount;
      return undefined;
    }

    const latestFollowUpRef = followUpResponseRefs.current[currentFollowUpCount - 1];
    previousFollowUpCountRef.current = currentFollowUpCount;

    if (!latestFollowUpRef) {
      return undefined;
    }

    return smoothScrollToElementTop(latestFollowUpRef);
  }, [followUpEntries.length]);

  useEffect(() => {
  let lng = localStorage.getItem('lng') ? localStorage.getItem('lng') : 'en';
  
  chatHistory.forEach((entry, index) => {
    const isOverrideActive = entry?.isOverride;
    
    if (isOverrideActive) {
      const overrideScenario = entry?.parsed?.actions?.stateDelta?.overrideScenario || entry?.overrideScenario;
      const aemOverrideURL = `${config.AEM_API_BASE_PATH}/content/experience-fragments/vsbdr/${config.aemUrlEnv}/${lng}/ai_custOverride/${overrideScenario}/master.html`;
      getInfoHubHtml(aemOverrideURL).then((response) => {
        setAemOverrideDataByIndex(prev => ({
          ...prev,
          [index]: response?.data
        }));
      });
    }
  });
}, [chatHistory]);
  const handleSourcesToggle = () => {
    if (isSourcesOpen) {
      setIsSourcesClosing(true);
    } else {
      setIsSourcesOpen(true);
    }
  };

  const handleSourcesPanelAnimationEnd = () => {
    if (isSourcesClosing) {
      setIsSourcesOpen(false);
      setIsSourcesClosing(false);
    }
  };

  const hasValidCourseData = (courseData) => {
    if (!courseData || !Array.isArray(courseData) || courseData.length === 0) return false;
    return courseData.some(item => {
      const itemType = (item.type || '').toLowerCase();
      if (!itemType || itemType === 'instructor' || itemType === 'spotlight' || itemType === 'page' || itemType === 'gated') return false;
      const category = (item.category || '').toLowerCase();
      return category.includes('event') || category.includes('course') || category.includes('tip');
    });
  };

  return (
    <ChatThread>
      {/* ── First / initial response ── */}
      <ResponseContent>
        {firstEntry?.query && (
          <div className="vds-title-md mobile-vds-title-sm font-eDS-bold mb-8X ph-6X sm-ph-4X">
            {firstEntry.query}
          </div>
        )}
        <Helmet>
              <link
                // href="https://mbqa1.sdc.vzwcorp.com/content/dam/vsbdr/clientlibs/QA2/css/herobanner.css"
                href={`${global.config.AEM_API_BASE_PATH}${global.config.aemUrlPath}${global.config.aemUrlEnv === 'QA1' ? '/QA1' : global.config.aemUrlEnv === 'QA2' ? '/QA2' : ''}/css/custOverride.css`}
                rel="stylesheet"
                type="text/css"
              ></link>
              {/* <link
                // href="https://mbqa1.sdc.vzwcorp.com/content/dam/vsbdr/clientlibs/QA2/css/common/typography.css"
                href={`${global.config.AEM_API_BASE_PATH}${global.config.aemUrlPath}${global.config.aemUrlEnv === 'QA1' ? '/QA1' : global.config.aemUrlEnv === 'QA2' ? '/QA2' : ''}/css/common/typography.css`}
                rel="stylesheet"
                type="text/css"
              ></link>
              <link
                // href="https://mbqa1.sdc.vzwcorp.com/content/dam/vsbdr/clientlibs/QA2/min/css/common/fonts.min.css"
                href={`${global.config.AEM_API_BASE_PATH}${global.config.aemUrlPath}${global.config.aemUrlEnv === 'QA1' ? '/QA1' : global.config.aemUrlEnv === 'QA2' ? '/QA2' : ''}/min/css/common/fonts.min.css`}
                rel="stylesheet"
                type="text/css"
              ></link> */}
              <script
                src={`${global.config.AEM_API_BASE_PATH}${global.config.aemUrlPath}${global.config.aemUrlEnv === 'QA1' ? '/QA1' : global.config.aemUrlEnv === 'QA2' ? '/QA2' : ''}/js/custOverride.js`}
                // src='https://mbqa1.sdc.vzwcorp.com/content/dam/vsbdr/clientlibs/QA2/js/herobanner.js'
                type='text/javascript'
                async
              ></script>
            </Helmet>
        {/* Show generating animation while streaming and no text yet */}
        {firstEntry?.isStreaming && !firstDisplayText ? (
          <AskAIGeneratingResponse />
        ) : (
           <> {firstEntry && firstEntry?.isOverride ? (
            <>
            <div>            
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(aemOverrideDataByIndex[0],{ADD_ATTR: ['target']}),
              }}
            ></div>
          </div>
            
            </>
            
          ):
          //(firstEntry?.tempFlag ? <GeneratingLabel>{firstEntry?.tempTxt}</GeneratingLabel> :
            <ResponseText text={firstDisplayText} isStreaming={firstEntry?.isStreaming} />
          }
            <AskAISuggestionResponse
              chatHistory={chatHistory}
              citations={firstEntry?.citations}
              isSourcesOpen={isSourcesOpen}
              isSourcesClosing={isSourcesClosing}
              handleSourcesToggle={handleSourcesToggle}
              handleSourcesPanelAnimationEnd={handleSourcesPanelAnimationEnd}
            />
           {firstEntry?.courseResults?.length > 0 && !(firstEntry.isOverride) && !(firstEntry.isSSEOverride) && (
              <AIRecommendationCarouselSection courseData={firstEntry?.courseResults} askAiData={askAiData} onViewAllMixed={(filterType) => onViewMixedResults(firstEntry.query, filterType)}/>
            )}
        </>
        )}

        {(firstEntry?.isError || firstEntry?.error) && (
          custOverrideXfHtml ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(custOverrideXfHtml,{ADD_ATTR: ['target']}),
              }}
            />
          ) : (
            <div className="vds-body-sm ph-6X sm-ph-4X mb-8X" style={{ color: '#b00020' }}>
              {firstEntry.error}
            </div>
          )
        )}

        

        {/* Follow-up question prompt + suggestions from API response */}
        {!firstEntry?.isStreaming && firstEntry?.followUpQuestions?.length > 0 && !(firstEntry.isOverride) &&
(
          <Flex flexDirection="column" marginTop={ firstEntry?.courseResults?.length === 0 ? '0px' : isMobile ? '48px' : "64px"} gap="32px" padding={isMobile ? '0 16px' : '0 24px'}>
            {firstEntry?.followUpQuestionText && (
              <div className="vds-title-xs mobile-vds-title-2xs">
                {renderFormattedText(firstEntry.followUpQuestionText)}
              </div>
            )}
            <Flex gap="12px" marginBottom="32px" flexWrap="wrap">
              {firstEntry.followUpQuestions.map((q, i) => (
                <SuggestedButton key={i} disabled={isChatLoading} onClick={() => { if (!isSearchDisabled && !isChatLoading) handleSuggestionClick(q); }}>
                  <div className="vds-body-lg font-eDS-bold mobile-vds-body-sm mobile-font-eTX-bold">
                    {q}
                  </div>
                </SuggestedButton>
              ))}
            </Flex>
          </Flex>
        )}

        {!firstEntry?.isStreaming && firstEntry?.finalText && (
          <Block padding={isMobile ? '0 16px' : '0 24px'}>
            <AskAIResponseFeedback
              selectedThumb={selectedThumb}
              onThumbClick={handleThumbClick}
              showFeedbackPanel={showFeedbackPanel}
              isFeedbackClosing={isFeedbackClosing}
              onCloseFeedback={closeFeedbackPanel}
              onFeedbackAnimationEnd={handleFeedbackAnimationEnd}
              isMobile={isMobile}
              vegasId={firstEntry?.vegasId}
            />
          </Block>
        )}
      </ResponseContent>

      {/* ── Follow-up chat entries ── */}
      {followUpEntries.map((entry, idx) => {
        const displayText = entry.streamingText || entry.finalText || '';
        const displayCitations = entry?.isOverride || entry?.isSSEOverride ? [] : entry?.citations?.length > 0 ? entry.citations : firstEntry?.citations;
        const displayCourseResults = entry?.isOverride || entry?.isSSEOverride
          ? []
          : hasValidCourseData(entry?.courseResults)
            ? entry.courseResults
            : hasValidCourseData(firstEntry?.courseResults)
              ? firstEntry.courseResults
              : [];
        return (
          <div key={idx} ref={(el) => { followUpResponseRefs.current[idx] = el; }}>
            {/* Follow-up question heading */}
            {entry.query && (
              <div className="vds-title-md mobile-vds-title-sm font-eDS-bold mb-8X ph-6X sm-ph-4X" style={{ marginTop: isMobile ? '64px' : isTablet ? '96px' : '128px' }}>
                {entry.query}
              </div>
            )}

            {/* AI response */}
            <ResponseContent style={{paddingTop: entry.query ? '0px' : undefined}}>
              {entry.isStreaming && !displayText ? (
                <AskAIGeneratingResponse />
              ) : (
                <>
                  {entry?.isOverride ? (
                    <>
                      <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(aemOverrideDataByIndex[idx+1]),
                      }}
                    ></div>
                    </>
                  ) : (
                    <ResponseText text={displayText} isStreaming={entry.isStreaming} />
                  )}
                  
                  {(entry.isError || entry.error) && (
                    custOverrideXfHtml ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(custOverrideXfHtml),
                        }}
                      />
                    ) : (
                      <div className="vds-body-sm ph-6X sm-ph-4X mb-8X" style={{ color: '#b00020' }}>
                        {entry.error}
                      </div>
                    )
                  )}

                { !(entry.isError || entry.error) && (  <AskAISuggestionResponse
                    chatHistory={chatHistory}
                    citations={displayCitations}
                    isSourcesOpen={isSourcesOpen}
                    isSourcesClosing={isSourcesClosing}
                    handleSourcesToggle={handleSourcesToggle}
                    handleSourcesPanelAnimationEnd={handleSourcesPanelAnimationEnd}
                  />)}
                  {displayCourseResults?.length > 0 && !(entry.isError || entry.error) && (
                    <AIRecommendationCarouselSection courseData={displayCourseResults} askAiData={askAiData} onViewAllMixed={(filterType) => onViewMixedResults(entry.query, filterType)}/>
                  )}
                  {/* Follow-up question prompt + suggestions for this entry */}
                  {!entry.isStreaming && entry.followUpQuestions?.length > 0 && !(entry.isOverride) && (
                    <Flex flexDirection="column" marginTop={displayCourseResults?.length === 0 ? '0px' : isMobile ? '48px' : "64px"} gap="32px" padding={isMobile ? '0 16px' : '0 24px'}>
                      {entry?.followUpQuestionText && (
                        <div className="vds-title-xs mobile-vds-title-2xs">
                       {renderFormattedText(entry.followUpQuestionText)}   
                        </div>
                      )}
                      <Flex gap="12px" marginBottom="32px" flexWrap="wrap">
                        {entry.followUpQuestions.map((q, i) => (
                          <SuggestedButton key={i} disabled={isChatLoading} onClick={() => { if (!isSearchDisabled && !isChatLoading) handleSuggestionClick(q); }}>
                            <div className="vds-body-lg font-eDS-bold mobile-vds-body-sm mobile-font-eTX-bold">
                              {q}
                            </div>
                          </SuggestedButton>
                        ))}
                      </Flex>
                    </Flex>
                  )}

                  {!entry.isStreaming && entry.finalText && (
                    <Block padding={isMobile ? '0 16px' : '0 24px'}>
                      <ChatEntryFeedback isMobile={isMobile} vegasId={entry?.vegasId} />
                    </Block>
                  )}
                </>
              )}
            </ResponseContent>
          </div>
        );
      })}

      {/* Loading indicator when a new follow-up is being sent */}
      {isChatLoading && chatHistory.length === 0 && (
        <ResponseContent>
          <AskAIGeneratingResponse />
        </ResponseContent>
      )}
    </ChatThread>
  );
};

export default AskAIInitialResponse;
